import { WorkAssemblyVersionRegistry } from './WorkAssemblyVersionRegistry'

/**
 * 单个运行时中的 WorkAssembly 活动版本会话。
 *
 * Registry 只负责不可变版本与并发构建；本类进一步收口“哪个版本当前活动”以及旧快照
 * 的退役重试。这样协议 Adapter 不需要理解快照缓存和失败释放的所有权规则。
 */
export class WorkAssemblySession {
  /** 不可变版本与同版本构建去重。 */
  private readonly registry = new WorkAssemblyVersionRegistry()

  /** 当前仍由场景与 Worker 支撑的活动版本。 */
  private activeVersionKey: PuppyCAD.WorkAssemblyVersionKey | null = null

  /** 已退出活动槽位、但上次释放失败的快照。 */
  private readonly pendingRetirements = new Set<PuppyCAD.WorkAssemblySnapshot>()

  /** 会话是否已经进入销毁阶段。 */
  private disposed = false

  /**
   * 解析目标版本并将其设为唯一活动快照。
   *
   * 新快照先发布为活动版本，再移出并释放旧快照。旧快照释放失败不会撤销已经成功的
   * 版本切换，而是保留在 pending 集合中供下一次操作重试。
   *
   * @param versionKey 目标不可变版本。
   * @param builder Registry 未命中时执行的完整构建事务。
   * @returns 活动快照、是否替换旧版本及本次退役错误。
   */
  public async resolve(
    versionKey: PuppyCAD.WorkAssemblyVersionKey,
    builder: PuppyCAD.WorkAssemblyBuilder,
  ): Promise<PuppyCAD.WorkAssemblySessionResolveResult> {
    this.assertAvailable()
    const retirementErrors = this.retryPendingRetirements()
    const snapshot = await this.registry.resolve(versionKey, builder)
    const previousVersionKey = this.activeVersionKey
    const replacedActiveSnapshot = Boolean(
      previousVersionKey
      && !this.isSameVersion(previousVersionKey, snapshot.versionKey),
    )

    this.activeVersionKey = snapshot.versionKey
    if (previousVersionKey && replacedActiveSnapshot) {
      const previousSnapshot = this.registry.take(previousVersionKey)
      if (previousSnapshot) {
        this.retireSnapshot(previousSnapshot, retirementErrors)
      }
    }

    return Object.freeze({ snapshot, replacedActiveSnapshot, retirementErrors })
  }

  /**
   * 查询当前活动快照。
   *
   * @param versionKey 可选的目标版本；提供时只有完全匹配才返回快照。
   * @returns 当前活动快照或 null。
   */
  public getActive(
    versionKey?: PuppyCAD.WorkAssemblyVersionKey,
  ): PuppyCAD.WorkAssemblySnapshot | null {
    this.assertAvailable()
    const activeVersionKey = this.activeVersionKey
    if (!activeVersionKey || (versionKey && !this.isSameVersion(activeVersionKey, versionKey))) {
      return null
    }
    return this.registry.get(activeVersionKey)
  }

  /**
   * 原子替换局部 revision 的活动快照，但不释放被替换快照。
   *
   * 局部 revision 会复用旧对象与资源，旧快照的所有权已经转交给新快照及资源 revision，
   * 因此这里必须只替换 Registry 槽位。
   *
   * @param snapshot 已完成外部交付校验的新快照。
   * @returns 被替换的旧快照。
   */
  public replaceActive(snapshot: PuppyCAD.WorkAssemblySnapshot): PuppyCAD.WorkAssemblySnapshot {
    this.assertAvailable()
    const previousVersionKey = this.activeVersionKey
    if (!previousVersionKey) {
      throw new Error('WorkAssembly 不存在可替换的活动快照')
    }
    const currentSnapshot = this.registry.get(previousVersionKey)
    if (!currentSnapshot) {
      throw new Error('WorkAssembly 活动快照已丢失，无法发布局部 revision')
    }
    const previousSnapshot = this.registry.replace(previousVersionKey, snapshot)
    this.activeVersionKey = snapshot.versionKey
    return previousSnapshot ?? currentSnapshot
  }

  /**
   * 失效并释放当前活动快照。
   *
   * @returns 存在活动快照时返回 true。
   */
  public invalidate(): boolean {
    this.assertAvailable()
    const retirementErrors = this.retryPendingRetirements()
    const activeVersionKey = this.activeVersionKey
    this.activeVersionKey = null
    if (!activeVersionKey) {
      this.throwRetirementErrors(retirementErrors)
      return false
    }
    const snapshot = this.registry.take(activeVersionKey)
    if (snapshot) {
      this.retireSnapshot(snapshot, retirementErrors)
    }
    this.throwRetirementErrors(retirementErrors)
    return Boolean(snapshot)
  }

  /**
   * 销毁会话并重试全部失败退役。
   *
   * 重复调用仍会重试 pending 快照；只有全部释放成功后才完全幂等返回。
   */
  public dispose(): void {
    const retirementErrors = this.retryPendingRetirements()
    if (!this.disposed) {
      const activeVersionKey = this.activeVersionKey
      this.activeVersionKey = null
      if (activeVersionKey) {
        const activeSnapshot = this.registry.take(activeVersionKey)
        if (activeSnapshot) {
          this.retireSnapshot(activeSnapshot, retirementErrors)
        }
      }
      try {
        this.registry.dispose()
      } catch (error) {
        retirementErrors.push(error)
      }
      this.disposed = true
    }
    this.throwRetirementErrors(retirementErrors)
  }

  /** 尝试释放一个快照；失败时保留其 owner 供后续重试。 */
  private retireSnapshot(
    snapshot: PuppyCAD.WorkAssemblySnapshot,
    errors: unknown[],
  ): void {
    try {
      snapshot.dispose()
      this.pendingRetirements.delete(snapshot)
    } catch (error) {
      this.pendingRetirements.add(snapshot)
      // 快照会聚合多个 package owner 的失败；Session 对外保持单层错误列表，便于调用方定位。
      if (error instanceof AggregateError) {
        errors.push(...error.errors)
      } else {
        errors.push(error)
      }
    }
  }

  /** 重试此前释放失败的全部快照，单项失败不阻断其它项。 */
  private retryPendingRetirements(): unknown[] {
    const errors: unknown[] = []
    for (const snapshot of [...this.pendingRetirements]) {
      this.retireSnapshot(snapshot, errors)
    }
    return errors
  }

  /** 版本身份必须同时匹配仓库与提交。 */
  private isSameVersion(
    left: PuppyCAD.WorkAssemblyVersionKey,
    right: PuppyCAD.WorkAssemblyVersionKey,
  ): boolean {
    return left.repositoryId.trim() === right.repositoryId.trim()
      && left.commitId.trim() === right.commitId.trim()
  }

  /** 聚合报告退役失败，同时保留失败快照供后续重试。 */
  private throwRetirementErrors(errors: readonly unknown[]): void {
    if (errors.length > 0) {
      throw new AggregateError(errors, 'WorkAssembly 快照资源释放失败')
    }
  }

  /** 销毁后禁止继续加载或替换版本。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('WorkAssembly 活动版本会话已销毁')
    }
  }
}
